import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "@/api/client";
import {
  streamChat,
  type AssistantStreamEvent,
  type ChatContext,
  type TurnUsage,
} from "./assistantStream";

export interface ToolCall {
  name: string;
  input: string;
}

export interface ChatMessage {
  role: "user" | "assistant";
  text: string;
  toolCalls: ToolCall[];
}

export type ChatStatus = "idle" | "streaming" | "error";

const SESSION_KEY = "assistant:sessionId";
const TRANSCRIPT_KEY = "assistant:transcript";

interface PersistedTranscript {
  messages: ChatMessage[];
  usage: TurnUsage | null;
}

function newSessionId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `s-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

function loadSessionId(): string {
  if (typeof window === "undefined") return newSessionId();
  const existing = window.sessionStorage.getItem(SESSION_KEY);
  if (existing) return existing;
  const id = newSessionId();
  window.sessionStorage.setItem(SESSION_KEY, id);
  return id;
}

/** Rehydrate the rendered transcript so it survives navigating away and back (per-tab). */
function loadTranscript(): PersistedTranscript {
  if (typeof window === "undefined") return { messages: [], usage: null };
  try {
    const raw = window.sessionStorage.getItem(TRANSCRIPT_KEY);
    if (!raw) return { messages: [], usage: null };
    const parsed = JSON.parse(raw) as PersistedTranscript;
    return { messages: parsed.messages ?? [], usage: parsed.usage ?? null };
  } catch {
    return { messages: [], usage: null };
  }
}

/**
 * Owns a single chat session: the message list, streaming status, token usage, and the
 * `sessionId` (kept in `sessionStorage` so it survives reloads but dies with the tab — by design,
 * matching the server-side memory's session scope). `send` streams a turn; `clear` drops the
 * server history and starts fresh. An in-flight stream is aborted on unmount or on `clear`.
 */
export function useAssistantChat() {
  const initial = loadTranscript();
  const [messages, setMessages] = useState<ChatMessage[]>(initial.messages);
  const [status, setStatus] = useState<ChatStatus>("idle");
  const [usage, setUsage] = useState<TurnUsage | null>(initial.usage);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const sessionIdRef = useRef<string>(loadSessionId());
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Persist the transcript so it's restored after navigating away and back (per-tab).
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(TRANSCRIPT_KEY, JSON.stringify({ messages, usage }));
  }, [messages, usage]);

  const send = useCallback(
    async (text: string, context?: ChatContext) => {
      const trimmed = text.trim();
      if (!trimmed || status === "streaming") return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setStatus("streaming");
      setActiveTool(null);
      // Append the user message and an empty assistant message we stream into.
      setMessages((prev) => [
        ...prev,
        { role: "user", text: trimmed, toolCalls: [] },
        { role: "assistant", text: "", toolCalls: [] },
      ]);

      const appendToAssistant = (mutate: (m: ChatMessage) => ChatMessage) =>
        setMessages((prev) => {
          const next = [...prev];
          const lastIdx = next.length - 1;
          const lastMsg = next[lastIdx];
          if (lastMsg && lastMsg.role === "assistant") next[lastIdx] = mutate(lastMsg);
          return next;
        });

      const onEvent = (event: AssistantStreamEvent) => {
        switch (event.type) {
          case "token":
            appendToAssistant((m) => ({ ...m, text: m.text + event.text }));
            break;
          case "tool_call":
            setActiveTool(event.name);
            appendToAssistant((m) => ({
              ...m,
              toolCalls: [...m.toolCalls, { name: event.name, input: event.input }],
            }));
            break;
          case "tool_result":
            setActiveTool(null);
            break;
          case "done":
            setUsage(event.usage);
            break;
          case "error":
            appendToAssistant((m) => ({
              ...m,
              text: m.text ? m.text : event.message,
            }));
            setStatus("error");
            break;
        }
      };

      await streamChat(
        { sessionId: sessionIdRef.current, message: trimmed, context },
        { onEvent, signal: controller.signal },
      );

      setActiveTool(null);
      // `error` already set the status; otherwise we're back to idle.
      setStatus((s) => (s === "error" ? "error" : "idle"));
    },
    [status],
  );

  const clear = useCallback(() => {
    abortRef.current?.abort();
    const id = sessionIdRef.current;
    // Best-effort server-side history clear; ignore failures (the new session id below is the
    // real reset — the old history just expires from the cache).
    void apiFetch(`/assistant/sessions/${encodeURIComponent(id)}`, { method: "DELETE" }).catch(() => {});
    const next = newSessionId();
    sessionIdRef.current = next;
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(SESSION_KEY, next);
      window.sessionStorage.removeItem(TRANSCRIPT_KEY);
    }
    setMessages([]);
    setUsage(null);
    setActiveTool(null);
    setStatus("idle");
  }, []);

  return { messages, status, usage, activeTool, send, clear };
}
